/**
 * Notification Center Component
 * Displays real-time alerts and notifications
 *
 * Now uses AlertContext for global state management.
 * WebSocket connection is maintained by AlertContext at app level.
 */

import React from 'react';
import { AlertTriangle, Bell, CheckCircle, X, AlertCircle, Zap, RefreshCw, CheckCheck, Loader2 } from 'lucide-react';
import { useAlertContext, CenterAlert } from '../contexts/AlertContext';

interface AlertCenterProps {
  maxAlerts?: number;
  autoHideDelay?: number;
  // Callback to pass unread count to parent (still supported)
  onUnreadCountChange?: (count: number) => void;
}

const AlertCenter: React.FC<AlertCenterProps> = ({
  onUnreadCountChange
}) => {
  // Use global AlertContext instead of internal WebSocket
  const {
    alerts,
    unreadCount,
    isConnected,
    isLoading,
    dismissAlert,
    acknowledgeAlert,
    reloadNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    deleteNotification
  } = useAlertContext();

  // Notify parent about unread count changes
  React.useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  // Get icon based on alert type
  const getAlertIcon = (type: CenterAlert['type']) => {
    switch (type) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5" />;
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  // Get alert style based on type
  const getAlertStyle = (type: CenterAlert['type']) => {
    switch (type) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800 bg-red-900/30 border-red-700 text-red-300';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800 bg-yellow-900/30 border-yellow-700 text-yellow-300';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800 bg-green-900/30 border-green-700 text-green-300';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800 bg-blue-900/30 border-blue-700 text-blue-300';
    }
  };

  // Format System log messages to be more human-readable
  const formatMessage = (alert: CenterAlert): string => {
    const message = alert.message || '';
    const title = alert.title || '';
    
    // Check if this is a System log message (connection status)
    if (title === 'System' || title.toLowerCase().includes('system')) {
      // Check for disconnect indicators
      if (message.toLowerCase().includes('disconnect') || 
          message.toLowerCase().includes('timeout') ||
          message.toLowerCase().includes('mqtt_keep_alive_timeout')) {
        return '已離線 (Disconnected)';
      }
      // Check for connect indicators or generic system messages with serviceName (usually means connected)
      if (message.includes('serviceName:') || 
          message.toLowerCase().includes('connect') ||
          message.includes('severity:')) {
        // If it contains disconnect-related info, it's disconnected
        if (message.toLowerCase().includes('disconnect')) {
          return '已離線 (Disconnected)';
        }
        // Otherwise assume connected
        return '已連線 (Connected)';
      }
    }
    
    return message;
  };

  // Handle clicking on a notification to mark it as read
  const handleNotificationClick = async (alert: CenterAlert) => {
    if (!alert.acknowledged) {
      await markNotificationRead(alert.id);
    }
  };

  // Handle marking all notifications as read
  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
  };

  // Handle deleting a notification
  const handleDeleteNotification = async (id: string) => {
    await deleteNotification(id);
  };

  return (
    <div className="alert-center">
      {/* Header with Connection Status and Actions */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2 text-white">
          <Bell className="w-5 h-5 text-yellow-500" />
          Notification
        </h3>
        <div className="flex items-center gap-2 pr-8">
          {/* Refresh Button */}
          <button
            onClick={reloadNotifications}
            disabled={isLoading}
            className="p-1.5 rounded-md hover:bg-slate-700/50 transition-colors disabled:opacity-50"
            title="Refresh notifications"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 text-slate-400" />
            )}
          </button>
          
          {/* Mark All Read Button */}
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="p-1.5 rounded-md hover:bg-slate-700/50 transition-colors"
              title="Mark all as read"
            >
              <CheckCheck className="w-4 h-4 text-slate-400" />
            </button>
          )}

          {/* Connection Status Icon */}
          <div 
            className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
            title={isConnected ? 'Connected' : 'Disconnected'}
          />
        </div>
      </div>

      {/* Alert List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {isLoading && alerts.length === 0 ? (
          <div className="text-center py-8 text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading notifications...
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            No active alerts
          </div>
        ) : (
          alerts.map(alert => (
            <div
              key={alert.id}
              onClick={() => handleNotificationClick(alert)}
              className={`
                p-4 border rounded-lg transition-all duration-300 cursor-pointer
                ${getAlertStyle(alert.type)}
                ${alert.acknowledged ? 'opacity-60' : ''}
                ${alert.type === 'critical' && !alert.acknowledged ? 'animate-pulse' : ''}
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {getAlertIcon(alert.type)}
                  <div className="flex-1">
                    <h4 className="font-semibold">{alert.title}</h4>
                    <p className="text-sm mt-1">{formatMessage(alert)}</p>
                    {alert.deviceSn && (
                      <p className="text-xs mt-1 opacity-75">
                        Device: {alert.deviceSn}
                      </p>
                    )}
                    <p className="text-xs mt-2 opacity-60">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  {!alert.acknowledged && alert.type === 'critical' && (
                    <button
                      onClick={() => acknowledgeAlert(alert.id)}
                      className="p-1 hover:bg-slate-800/50/20 rounded"
                      title="Acknowledge"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteNotification(alert.id)}
                    className="p-1 hover:bg-slate-800/50/20 rounded text-red-500 hover:text-red-600"
                    title="Delete notification"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary Stats */}
      {alerts.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-700/30 flex gap-4 text-sm">
          <span className="text-red-400">
            Critical: {alerts.filter(a => a.type === 'critical' && !a.acknowledged).length}
          </span>
          <span className="text-yellow-600 text-yellow-400">
            Warnings: {alerts.filter(a => a.type === 'warning').length}
          </span>
          <span className="text-slate-400">
            Total: {alerts.length}
          </span>
        </div>
      )}
    </div>
  );
};

export default AlertCenter;
